// Spine admin API client. Calls the spine Worker's /admin-api/* routes,
// authenticating with a Shopify session token from App Bridge.
//
// App Bridge v4 exposes `window.shopify` as a global once the
// <script src="https://cdn.shopify.com/shopifycloud/app-bridge.js"> tag in
// index.html has loaded with the shopify-api-key meta tag present. The
// global gives us .idToken() which returns a JWT signed by Shopify.

const SPINE_BASE = 'https://spine.herbanvault.workers.dev';

interface ShopifyAppBridge {
  idToken: () => Promise<string>;
}

declare global {
  interface Window {
    shopify?: ShopifyAppBridge;
  }
}

async function getAuthHeader(): Promise<string> {
  if (!window.shopify) {
    throw new Error('App Bridge not loaded. Are you running outside Shopify Admin?');
  }
  const token = await window.shopify.idToken();
  return `Bearer ${token}`;
}

export interface AdminReview {
  id: string;
  product_id: string;
  product_title: string | null;
  product_url: string | null;
  customer_id: string;
  rating: number;
  title: string | null;
  body: string;
  status: 'pending' | 'approved' | 'rejected';
  created_at: string;
  approved_at: string | null;
  rejected_at: string | null;
  moderation_note: string | null;
  display_name: string | null;
  verified_purchase: boolean;
  photos: Array<{ id: string; url: string; content_type: string }>;
}

export interface AdminReviewsResponse {
  ok: boolean;
  status: 'pending' | 'approved' | 'rejected';
  page: number;
  limit: number;
  total: number;
  counts: { pending: number; approved: number; rejected: number };
  reviews: AdminReview[];
}

export async function listReviews(
  status: 'pending' | 'approved' | 'rejected',
  page = 1,
): Promise<AdminReviewsResponse> {
  const auth = await getAuthHeader();
  const res = await fetch(`${SPINE_BASE}/admin-api/reviews?status=${status}&page=${page}`, {
    headers: { Authorization: auth },
  });
  if (!res.ok) {
    const body = await res.text();
    throw new Error(`listReviews failed (${res.status}): ${body}`);
  }
  return (await res.json()) as AdminReviewsResponse;
}

export async function approveReview(reviewId: string, note?: string): Promise<void> {
  const auth = await getAuthHeader();
  const res = await fetch(`${SPINE_BASE}/admin-api/reviews/${reviewId}/approve`, {
    method: 'POST',
    headers: { Authorization: auth, 'Content-Type': 'application/json' },
    body: JSON.stringify({ note: note ?? null }),
  });
  if (!res.ok) {
    throw new Error(`approveReview failed (${res.status})`);
  }
}

export async function rejectReview(reviewId: string, note?: string): Promise<void> {
  const auth = await getAuthHeader();
  const res = await fetch(`${SPINE_BASE}/admin-api/reviews/${reviewId}/reject`, {
    method: 'POST',
    headers: { Authorization: auth, 'Content-Type': 'application/json' },
    body: JSON.stringify({ note: note ?? null }),
  });
  if (!res.ok) {
    throw new Error(`rejectReview failed (${res.status})`);
  }
}

export async function deleteReview(reviewId: string): Promise<void> {
  const auth = await getAuthHeader();
  const res = await fetch(`${SPINE_BASE}/admin-api/reviews/${reviewId}`, {
    method: 'DELETE',
    headers: { Authorization: auth },
  });
  if (!res.ok) {
    throw new Error(`deleteReview failed (${res.status})`);
  }
}
